import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  reputation_points: number;
  bio: string | null;
  location: string | null;
  country: string | null;
  city: string | null;
  neighborhood: string | null;
  first_name: string | null;
  last_name: string | null;
  interests: string[] | null;
  onboarded: boolean;
  onboarding_step: number;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (userId: string) => {
    const select = "id, username, full_name, avatar_url, reputation_points, bio, location, country, city, neighborhood, first_name, last_name, interests, onboarded, onboarding_step";
    const { data } = await supabase
      .from("profiles")
      .select(select)
      .eq("id", userId)
      .maybeSingle();
    if (data) { setProfile(data as Profile); return; }
    // First sign-in / missing row: create the profile + starter dashboard data, then retry.
    const { error } = await supabase.rpc("ensure_my_profile" as never);
    if (error) { setProfile(null); return; }
    const { data: healed } = await supabase
      .from("profiles")
      .select(select)
      .eq("id", userId)
      .maybeSingle();
    setProfile((healed ?? null) as Profile | null);
  };


  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        setTimeout(() => loadProfile(newSession.user.id), 0);
      } else {
        setProfile(null);
      }
    });

    supabase.auth.getSession().then(({ data: { session: initial } }) => {
      setSession(initial);
      setUser(initial?.user ?? null);
      if (initial?.user) loadProfile(initial.user.id);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshProfile = async () => {
    if (user) await loadProfile(user.id);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
